/**
 * Prime Pet Food — Dog Profile System
 * Persists dog data to localStorage, powers personalization across the store.
 * 
 * API:
 *   PrimeDogProfile.get()           → returns profile object or null
 *   PrimeDogProfile.save(data)      → saves/updates profile
 *   PrimeDogProfile.clear()         → removes profile
 *   PrimeDogProfile.hasProfile()    → boolean
 *   PrimeDogProfile.getRecommendation() → { size, duration, variant_id }
 *   PrimeDogProfile.getPersonality() → personality type string
 *   PrimeDogProfile.trackSession(data) → logs a chew session
 *   PrimeDogProfile.getSessions()   → array of past sessions
 *   PrimeDogProfile.getReorderTiming() → { daysUntilReorder, lastPurchase }
 *   PrimeDogProfile.onUpdate(fn)    → register callback for profile changes
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'prime_dog_profile';
  var SESSIONS_KEY = 'prime_chew_sessions';
  var PURCHASES_KEY = 'prime_purchases';
  var ENRICHMENT_HISTORY_KEY = 'prime_enrichment_history';
  var DURATION_REPORTS_KEY = 'prime_duration_reports';
  var listeners = [];

  /* ═══════════════════════════════════════
     BREED DATABASE — Duration modifiers
     ═══════════════════════════════════════ */
  var BREED_DATA = {
    'labrador': { modifier: 0.85, size: 'large', personality: 'enthusiastic', chewIntensity: 'power' },
    'golden_retriever': { modifier: 0.9, size: 'large', personality: 'gentle', chewIntensity: 'moderate' },
    'german_shepherd': { modifier: 0.8, size: 'large', personality: 'focused', chewIntensity: 'power' },
    'french_bulldog': { modifier: 1.1, size: 'small', personality: 'playful', chewIntensity: 'moderate' },
    'bulldog': { modifier: 0.95, size: 'medium', personality: 'relaxed', chewIntensity: 'power' },
    'poodle': { modifier: 1.2, size: 'medium', personality: 'intelligent', chewIntensity: 'gentle' },
    'beagle': { modifier: 1.0, size: 'medium', personality: 'curious', chewIntensity: 'moderate' },
    'rottweiler': { modifier: 0.7, size: 'large', personality: 'determined', chewIntensity: 'power' },
    'yorkshire_terrier': { modifier: 1.4, size: 'small', personality: 'feisty', chewIntensity: 'gentle' },
    'boxer': { modifier: 0.85, size: 'large', personality: 'energetic', chewIntensity: 'power' },
    'dachshund': { modifier: 1.2, size: 'small', personality: 'persistent', chewIntensity: 'moderate' },
    'shih_tzu': { modifier: 1.3, size: 'small', personality: 'gentle', chewIntensity: 'gentle' },
    'husky': { modifier: 0.75, size: 'large', personality: 'independent', chewIntensity: 'power' },
    'corgi': { modifier: 1.05, size: 'medium', personality: 'playful', chewIntensity: 'moderate' },
    'great_dane': { modifier: 0.65, size: 'xlarge', personality: 'gentle', chewIntensity: 'power' },
    'chihuahua': { modifier: 1.5, size: 'xsmall', personality: 'feisty', chewIntensity: 'gentle' },
    'pit_bull': { modifier: 0.65, size: 'large', personality: 'determined', chewIntensity: 'power' },
    'australian_shepherd': { modifier: 0.9, size: 'medium', personality: 'intelligent', chewIntensity: 'moderate' },
    'cavalier_king_charles': { modifier: 1.3, size: 'small', personality: 'gentle', chewIntensity: 'gentle' },
    'border_collie': { modifier: 0.95, size: 'medium', personality: 'intelligent', chewIntensity: 'moderate' },
    'doberman': { modifier: 0.75, size: 'large', personality: 'focused', chewIntensity: 'power' },
    'bernese_mountain_dog': { modifier: 0.8, size: 'xlarge', personality: 'gentle', chewIntensity: 'moderate' },
    'pomeranian': { modifier: 1.4, size: 'xsmall', personality: 'playful', chewIntensity: 'gentle' },
    'maltese': { modifier: 1.5, size: 'xsmall', personality: 'gentle', chewIntensity: 'gentle' },
    'cocker_spaniel': { modifier: 1.1, size: 'medium', personality: 'gentle', chewIntensity: 'moderate' },
    'jack_russell': { modifier: 0.9, size: 'small', personality: 'energetic', chewIntensity: 'power' },
    'mixed': { modifier: 1.0, size: 'medium', personality: 'unique', chewIntensity: 'moderate' },
    'other': { modifier: 1.0, size: 'medium', personality: 'unique', chewIntensity: 'moderate' }
  };

  /* ═══════════════════════════════════════
     SIZE → VARIANT MAPPING
     ═══════════════════════════════════════ */
  var SIZE_RECOMMENDATIONS = {
    'xsmall': { size: 'Small (Under 15 lbs)', variantTitle: 'Small' },
    'small': { size: 'Small (15-25 lbs)', variantTitle: 'Small' },
    'medium': { size: 'Medium (25-50 lbs)', variantTitle: 'Medium' },
    'large': { size: 'Large (50-80 lbs)', variantTitle: 'Large' },
    'xlarge': { size: 'Extra Large (80+ lbs)', variantTitle: 'Extra Large' }
  };

  var VARIANT_IDS = {
    'Small': '51744916701504',
    'Medium': '51744918962496',
    'Large': '51744918995264',
    'Extra Large': '51744919028032',
    'Jumbo': '51744919060800'
  };

  /* ═══════════════════════════════════════
     PERSONALITY SYSTEM
     ═══════════════════════════════════════ */
  var PERSONALITIES = {
    'power_chewer': {
      label: 'Power Chewer',
      emoji: '💪',
      description: 'Your dog is a determined chewer who needs the toughest, longest-lasting options.',
      tips: ['Choose our XL size for maximum duration', 'Try freezing the chew for extra challenge', 'Rotate between 2-3 chews to maintain interest']
    },
    'gentle_gnawer': {
      label: 'Gentle Gnawer',
      emoji: '🐾',
      description: 'Your dog takes their time and savors every chew session.',
      tips: ['Standard sizes work great for your pup', 'One chew can last multiple sessions', 'Great candidate for our subscription plan']
    },
    'anxious_chewer': {
      label: 'Stress Reliever',
      emoji: '🧘',
      description: 'Your dog uses chewing as a calming mechanism — and that\'s healthy!',
      tips: ['Keep a chew available during stressful times', 'Pair with a calming routine before departures', 'Consider our multi-pack for always having one ready']
    },
    'playful_nibbler': {
      label: 'Playful Nibbler',
      emoji: '🎾',
      description: 'Your dog alternates between chewing and playing — short bursts of focused gnawing.',
      tips: ['Smaller sizes are perfect for play-chew-play cycles', 'The puff treat makes a great quick reward', 'Try using the chew as a post-walk wind-down']
    },
    'focused_worker': {
      label: 'Focused Worker',
      emoji: '🎯',
      description: 'When your dog chews, they\'re ALL in. Intense focus until the job is done.',
      tips: ['Our large sizes provide the longest engagement', 'Use chew time as your productive work window', 'Track sessions to optimize your routine']
    }
  };

  /* ═══════════════════════════════════════
     CORE PROFILE METHODS
     ═══════════════════════════════════════ */
  function getProfile() {
    try {
      var data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  function saveProfile(data) {
    try {
      var existing = getProfile() || {};
      var updated = Object.assign({}, existing, data, {
        updatedAt: new Date().toISOString()
      });

      // Auto-calculate derived fields
      if (updated.breed && BREED_DATA[updated.breed]) {
        var breedInfo = BREED_DATA[updated.breed];
        updated.breedData = breedInfo;
        if (!updated.recommendedSize) {
          updated.recommendedSize = breedInfo.size;
        }
      }

      // Calculate personality if we have enough data
      if (updated.chewStyle && updated.breed) {
        updated.personality = calculatePersonality(updated);
      }

      // Set creation date if new
      if (!updated.createdAt) {
        updated.createdAt = new Date().toISOString();
      }

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      notifyListeners(updated);
      return updated;
    } catch (e) {
      console.warn('PrimeDogProfile: Could not save profile', e);
      return null;
    }
  }

  function clearProfile() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(SESSIONS_KEY);
      localStorage.removeItem(PURCHASES_KEY);
      localStorage.removeItem(ENRICHMENT_HISTORY_KEY);
      localStorage.removeItem(DURATION_REPORTS_KEY);
      notifyListeners(null);
    } catch (e) {}
  }

  function hasProfile() {
    return getProfile() !== null;
  }

  /* ═══════════════════════════════════════
     PERSONALITY CALCULATION
     ═══════════════════════════════════════ */
  function calculatePersonality(profile) {
    var chewStyle = profile.chewStyle || 'moderate';
    var breedData = profile.breedData || {};
    var age = profile.age || 'adult';

    // Determine personality based on chew style + breed traits
    if (chewStyle === 'destroyer' || breedData.chewIntensity === 'power') {
      if (age === 'puppy') return 'playful_nibbler';
      return 'power_chewer';
    }

    if (chewStyle === 'gentle' || breedData.chewIntensity === 'gentle') {
      return 'gentle_gnawer';
    }

    if (profile.chewReason === 'anxiety' || profile.chewReason === 'separation') {
      return 'anxious_chewer';
    }

    if (breedData.personality === 'intelligent' || breedData.personality === 'focused') {
      return 'focused_worker';
    }

    if (breedData.personality === 'playful' || breedData.personality === 'energetic') {
      return 'playful_nibbler';
    }

    return 'gentle_gnawer';
  }

  /* ═══════════════════════════════════════
     RECOMMENDATION ENGINE
     ═══════════════════════════════════════ */
  function getRecommendation() {
    var profile = getProfile();
    if (!profile) return null;

    var breed = profile.breed || 'mixed';
    var weight = profile.weight || 30;
    var chewStyle = profile.chewStyle || 'moderate';
    var age = profile.age || 'adult';

    var breedData = BREED_DATA[breed] || BREED_DATA['mixed'];

    // Calculate duration estimate
    var baseDuration = 60; // minutes
    var duration = baseDuration * breedData.modifier;

    // Weight adjustments
    if (weight < 15) duration *= 1.4;
    else if (weight < 25) duration *= 1.2;
    else if (weight < 50) duration *= 1.0;
    else if (weight < 80) duration *= 0.85;
    else duration *= 0.7;

    // Chew style adjustments
    if (chewStyle === 'destroyer') duration *= 0.6;
    else if (chewStyle === 'gentle') duration *= 1.4;

    // Age adjustments
    if (age === 'puppy') duration *= 1.3;
    else if (age === 'senior') duration *= 1.2;

    duration = Math.round(duration);

    // Determine recommended size
    var size = 'medium';
    if (weight < 15) size = 'xsmall';
    else if (weight < 25) size = 'small';
    else if (weight < 50) size = 'medium';
    else if (weight < 80) size = 'large';
    else size = 'xlarge';

    var sizeInfo = SIZE_RECOMMENDATIONS[size];

    return {
      duration: duration,
      durationRange: Math.round(duration * 0.75) + '-' + Math.round(duration * 1.25) + ' minutes',
      size: sizeInfo.size,
      variantTitle: sizeInfo.variantTitle,
      variantId: VARIANT_IDS[sizeInfo.variantTitle] || null,
      personality: profile.personality || calculatePersonality(profile),
      breed: breed,
      confidence: profile.breed !== 'other' ? 'high' : 'moderate'
    };
  }

  function calculateEnrichmentScore(profile, sessions) {
    profile = profile || getProfile() || {};
    sessions = sessions || getSessions();

    var score = 52;
    var chewStyle = profile.chewStyle || 'moderate';
    var rec = getRecommendation();
    var stats = getSessionStats();

    if (rec) score += 10;
    if (profile.breed && profile.breed !== 'other') score += 8;
    if (profile.weight) score += 8;
    if (chewStyle === 'destroyer') score += 8;
    if (profile.age) score += 4;
    if (stats) {
      if (stats.totalSessions >= 1) score += 6;
      if (stats.totalSessions >= 3) score += 6;
      if (stats.averageDuration >= 35) score += 4;
      if (stats.streak >= 3) score += 4;
    }

    return Math.max(0, Math.min(100, Math.round(score)));
  }

  function saveEnrichmentSnapshot(source) {
    try {
      var history = getEnrichmentHistory();
      var profile = getProfile();
      var stats = getSessionStats();
      var snapshot = {
        id: Date.now().toString(36),
        date: new Date().toISOString(),
        score: calculateEnrichmentScore(profile),
        source: source || 'profile',
        totalSessions: stats ? stats.totalSessions : 0,
        averageDuration: stats ? stats.averageDuration : null
      };

      history.push(snapshot);
      if (history.length > 90) history = history.slice(-90);
      localStorage.setItem(ENRICHMENT_HISTORY_KEY, JSON.stringify(history));
      return snapshot;
    } catch (e) {
      return null;
    }
  }

  function getEnrichmentHistory() {
    try {
      var data = localStorage.getItem(ENRICHMENT_HISTORY_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  /* ═══════════════════════════════════════
     CHEW SESSION TRACKING
     ═══════════════════════════════════════ */
  function trackSession(data) {
    try {
      var sessions = getSessions();
      sessions.push(Object.assign({}, data, {
        id: Date.now().toString(36),
        date: new Date().toISOString()
      }));

      // Keep last 50 sessions
      if (sessions.length > 50) {
        sessions = sessions.slice(-50);
      }

      localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
      saveEnrichmentSnapshot('session');
      notifyListeners(getProfile());
      return sessions;
    } catch (e) {
      return [];
    }
  }

  function getSessions() {
    try {
      var data = localStorage.getItem(SESSIONS_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function getSessionStats() {
    var sessions = getSessions();
    if (sessions.length === 0) return null;

    var totalDuration = 0;
    var count = sessions.length;

    sessions.forEach(function(s) {
      totalDuration += (s.duration || 0);
    });

    return {
      totalSessions: count,
      averageDuration: Math.round(totalDuration / count),
      totalMinutes: totalDuration,
      lastSession: sessions[sessions.length - 1],
      streak: calculateStreak(sessions)
    };
  }

  function calculateStreak(sessions) {
    if (sessions.length === 0) return 0;

    var streak = 1;
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    var lastDate = new Date(sessions[sessions.length - 1].date);
    lastDate.setHours(0, 0, 0, 0);

    // If last session wasn't today or yesterday, streak is 0
    var diffDays = Math.floor((today - lastDate) / (1000 * 60 * 60 * 24));
    if (diffDays > 1) return 0;

    for (var i = sessions.length - 2; i >= 0; i--) {
      var current = new Date(sessions[i].date);
      current.setHours(0, 0, 0, 0);
      var prev = new Date(sessions[i + 1].date);
      prev.setHours(0, 0, 0, 0);

      var gap = Math.floor((prev - current) / (1000 * 60 * 60 * 24));
      if (gap <= 1) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  }

  /* ═══════════════════════════════════════
     PURCHASE & REORDER TRACKING
     ═══════════════════════════════════════ */
  function trackPurchase(data) {
    try {
      var purchases = getPurchases();
      purchases.push(Object.assign({}, data, {
        date: new Date().toISOString()
      }));
      localStorage.setItem(PURCHASES_KEY, JSON.stringify(purchases));
      saveEnrichmentSnapshot('purchase');
      notifyListeners(getProfile());
      return purchases;
    } catch (e) {
      return [];
    }
  }

  function getPurchases() {
    try {
      var data = localStorage.getItem(PURCHASES_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  function getReorderTiming() {
    var purchases = getPurchases();
    var sessions = getSessions();
    var profile = getProfile();

    if (purchases.length === 0) {
      return { daysUntilReorder: null, lastPurchase: null, suggestion: 'Make your first purchase to start tracking!' };
    }

    var lastPurchase = purchases[purchases.length - 1];
    var daysSincePurchase = Math.floor((Date.now() - new Date(lastPurchase.date).getTime()) / (1000 * 60 * 60 * 24));

    // Estimate consumption rate
    var avgSessionsPerWeek = 5; // default
    var avgDurationPerSession = 60; // minutes default

    if (sessions.length >= 3) {
      var recentSessions = sessions.slice(-7);
      var timeSpan = (new Date(recentSessions[recentSessions.length - 1].date) - new Date(recentSessions[0].date)) / (1000 * 60 * 60 * 24);
      if (timeSpan > 0) {
        avgSessionsPerWeek = Math.round((recentSessions.length / timeSpan) * 7);
      }
      avgDurationPerSession = recentSessions.reduce(function(sum, s) { return sum + (s.duration || 60); }, 0) / recentSessions.length;
    }

    // A typical chew lasts ~10 hours of total chew time before consumed
    var totalChewHours = 10;
    var minutesPerWeek = avgSessionsPerWeek * avgDurationPerSession;
    var weeksPerChew = (totalChewHours * 60) / minutesPerWeek;
    var quantity = lastPurchase.quantity || 1;
    var totalWeeks = weeksPerChew * quantity;
    var daysUntilReorder = Math.round(totalWeeks * 7) - daysSincePurchase;

    var suggestion;
    if (daysUntilReorder <= 0) {
      suggestion = 'Time to reorder! Your supply may be running low.';
    } else if (daysUntilReorder <= 7) {
      suggestion = 'Running low soon — reorder this week for uninterrupted enrichment.';
    } else {
      suggestion = 'You\'re set for about ' + daysUntilReorder + ' more days.';
    }

    return {
      daysUntilReorder: Math.max(0, daysUntilReorder),
      daysSincePurchase: daysSincePurchase,
      lastPurchase: lastPurchase,
      suggestion: suggestion,
      avgSessionsPerWeek: avgSessionsPerWeek
    };
  }

  function getSubscriptionCadence() {
    var profile = getProfile() || {};
    var stats = getSessionStats();
    var rec = getRecommendation();
    var weight = profile.weight || 30;
    var chewStyle = profile.chewStyle || 'moderate';
    var sessionsPerWeek = 4;
    var avgDuration = rec ? rec.duration : 60;

    if (stats) {
      sessionsPerWeek = Math.max(2, Math.min(10, Math.round((stats.totalSessions / Math.max(1, Math.min(14, stats.totalSessions))) * 5)));
      avgDuration = stats.averageDuration || avgDuration;
    }

    var intervalDays = 30;
    var quantity = 2;

    if (chewStyle === 'destroyer' || weight >= 60) {
      intervalDays = 21;
      quantity = 3;
    } else if (chewStyle === 'gentle' && weight < 35) {
      intervalDays = 45;
      quantity = 2;
    }

    if (sessionsPerWeek >= 6 || avgDuration < 35) {
      intervalDays = Math.max(14, intervalDays - 7);
      quantity += 1;
    }

    return {
      intervalDays: intervalDays,
      quantity: quantity,
      size: rec ? rec.variantTitle : 'Medium',
      variantId: rec ? rec.variantId : VARIANT_IDS.Medium,
      reason: 'Based on dog size, chewing style, and logged session pace.',
      label: 'Every ' + intervalDays + ' days'
    };
  }

  function getPostPurchasePlan() {
    var rec = getRecommendation();
    var profile = getProfile() || {};
    var cadence = getSubscriptionCadence();

    return {
      intro: 'Start with short supervised sessions and track real chew time to improve future recommendations.',
      steps: [
        'Offer the chew after activity or during a calm indoor routine.',
        'Supervise every session and keep fresh water nearby.',
        'Remove the chew when it becomes small enough to swallow.',
        'Log the session in My Dog to tune reorder timing and subscription frequency.'
      ],
      recommendedSize: rec ? rec.variantTitle : 'Medium',
      cadence: cadence.label,
      dogName: profile.dogName || 'your dog'
    };
  }

  function savePhotoAnalysis(data) {
    try {
      var profile = getProfile() || {};
      var payload = Object.assign({}, data, {
        updatedAt: new Date().toISOString(),
        status: data && data.status ? data.status : 'local_preview'
      });
      return saveProfile({ photoAnalysis: payload });
    } catch (e) {
      return null;
    }
  }

  function captureEmail(email, source) {
    if (!email) return null;
    return saveProfile({
      email: email,
      emailCapturedAt: new Date().toISOString(),
      emailSource: source || 'profile_dashboard'
    });
  }

  function submitDurationReport(data) {
    try {
      var reports = getDurationReports();
      var profile = getProfile() || {};
      var report = Object.assign({}, data, {
        id: Date.now().toString(36),
        date: new Date().toISOString(),
        breed: data.breed || profile.breed || 'mixed',
        weight: data.weight || profile.weight || null,
        chewStyle: data.chewStyle || profile.chewStyle || null
      });
      reports.push(report);
      if (reports.length > 40) reports = reports.slice(-40);
      localStorage.setItem(DURATION_REPORTS_KEY, JSON.stringify(reports));
      notifyListeners(profile);
      return report;
    } catch (e) {
      return null;
    }
  }

  function getDurationReports() {
    try {
      var data = localStorage.getItem(DURATION_REPORTS_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  /* ═══════════════════════════════════════
     ENRICHMENT RECOMMENDATIONS
     ═══════════════════════════════════════ */
  function getEnrichmentTips() {
    var profile = getProfile();
    var sessions = getSessions();
    var personality = profile ? profile.personality : null;

    var tips = [];

    // Personality-based tips
    if (personality && PERSONALITIES[personality]) {
      tips = tips.concat(PERSONALITIES[personality].tips);
    }

    // Session-based tips
    var stats = getSessionStats();
    if (stats) {
      if (stats.averageDuration < 30) {
        tips.push('Try a larger size for longer engagement');
        tips.push('Freeze the chew to slow down power chewers');
      }
      if (stats.streak >= 7) {
        tips.push('Amazing ' + stats.streak + '-day streak! Consistency builds calm habits.');
      }
      if (stats.totalSessions >= 10) {
        tips.push('Your dog has completed ' + stats.totalSessions + ' enrichment sessions!');
      }
    }

    var cadence = getSubscriptionCadence();
    if (cadence && cadence.intervalDays <= 21) {
      tips.push('Keep a backup chew on hand so calm-time routines do not run out.');
    }

    return tips.slice(0, 5);
  }

  /* ═══════════════════════════════════════
     BREED-SPECIFIC MESSAGING
     ═══════════════════════════════════════ */
  function getBreedMessage() {
    var profile = getProfile();
    if (!profile || !profile.breed) return null;

    var breed = profile.breed;
    var breedData = BREED_DATA[breed];
    if (!breedData) return null;

    var dogName = profile.dogName || 'your dog';
    var breedLabel = breed.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); });

    var messages = {
      power: {
        headline: dogName + ' is a power chewer',
        subtext: breedLabel + 's are known for their strong jaws. Our XL chews are built to last.',
        badge: 'Power Chewer Approved'
      },
      moderate: {
        headline: 'Perfect for ' + dogName,
        subtext: breedLabel + 's typically enjoy 45-90 minutes of focused chewing per session.',
        badge: 'Breed-Matched'
      },
      gentle: {
        headline: dogName + ' will love this',
        subtext: breedLabel + 's are gentle chewers — one chew can last multiple sessions!',
        badge: 'Long-Lasting Choice'
      }
    };

    return messages[breedData.chewIntensity] || messages.moderate;
  }

  /* ═══════════════════════════════════════
     EVENT SYSTEM
     ═══════════════════════════════════════ */
  function onUpdate(fn) {
    if (typeof fn === 'function') {
      listeners.push(fn);
    }
  }

  function notifyListeners(profile) {
    listeners.forEach(function(fn) {
      try { fn(profile); } catch (e) {}
    });

    // Also dispatch a custom event for other scripts
    try {
      window.dispatchEvent(new CustomEvent('prime:profile:updated', { detail: profile }));
    } catch (e) {}
  }

  /* ═══════════════════════════════════════
     INTEGRATION: Auto-save from analyzer
     ═══════════════════════════════════════ */
  function initAnalyzerIntegration() {
    function normalizeAnalyzerBreed(breed) {
      var mapped = {
        'golden-retriever': 'golden_retriever',
        'labrador-retriever': 'labrador',
        'german-shepherd': 'german_shepherd',
        'french-bulldog': 'french_bulldog',
        'yorkshire-terrier': 'yorkshire_terrier',
        'siberian-husky': 'husky',
        'great-dane': 'great_dane',
        'australian-shepherd': 'australian_shepherd',
        'cavalier-king-charles': 'cavalier_king_charles',
        'shih-tzu': 'shih_tzu',
        'border-collie': 'border_collie',
        'pit-bull': 'pit_bull',
        'jack-russell': 'jack_russell',
        'bernese-mountain-dog': 'bernese_mountain_dog',
        'mixed-breed': 'mixed'
      };
      return mapped[breed] || (breed || 'mixed').replace(/-/g, '_');
    }

    function normalizeAnalyzerWeight(weight) {
      var mapped = {
        'under-15': 12,
        '15-35': 25,
        '35-60': 48,
        '60-plus': 70
      };
      return mapped[weight] || weight || 30;
    }

    function normalizeAnalyzerChewStyle(chewStyle) {
      if (chewStyle === 'aggressive') return 'destroyer';
      return chewStyle || 'moderate';
    }

    // Listen for analyzer completion events
    window.addEventListener('prime:analyzer:complete', function(e) {
      if (e.detail) {
        saveProfile({
          breed: normalizeAnalyzerBreed(e.detail.breed),
          weight: normalizeAnalyzerWeight(e.detail.weight),
          chewStyle: normalizeAnalyzerChewStyle(e.detail.chewStyle),
          age: e.detail.age,
          dogName: e.detail.dogName || null,
          analyzerBreed: e.detail.breed,
          analyzerWeight: e.detail.weight,
          analyzerChewStyle: e.detail.chewStyle,
          analyzerDurationRange: e.detail.durationRange,
          analyzerEnrichment: e.detail.enrichment,
          recommendedVariantId: e.detail.recommendedVariantId,
          source: 'analyzer'
        });
      }
    });
  }

  /* ═══════════════════════════════════════
     PUBLIC API
     ═══════════════════════════════════════ */
  window.PrimeDogProfile = {
    get: getProfile,
    save: saveProfile,
    clear: clearProfile,
    hasProfile: hasProfile,
    getRecommendation: getRecommendation,
    getPersonality: function() {
      var profile = getProfile();
      if (!profile || !profile.personality) return null;
      return PERSONALITIES[profile.personality] || null;
    },
    getPersonalityType: function() {
      var profile = getProfile();
      return profile ? profile.personality : null;
    },
    trackSession: trackSession,
    getSessions: getSessions,
    getSessionStats: getSessionStats,
    getEnrichmentScore: function() { return calculateEnrichmentScore(); },
    getEnrichmentHistory: getEnrichmentHistory,
    saveEnrichmentSnapshot: saveEnrichmentSnapshot,
    trackPurchase: trackPurchase,
    getPurchases: getPurchases,
    getReorderTiming: getReorderTiming,
    getSubscriptionCadence: getSubscriptionCadence,
    getPostPurchasePlan: getPostPurchasePlan,
    savePhotoAnalysis: savePhotoAnalysis,
    captureEmail: captureEmail,
    submitDurationReport: submitDurationReport,
    getDurationReports: getDurationReports,
    getEnrichmentTips: getEnrichmentTips,
    getBreedMessage: getBreedMessage,
    getBreedData: function(breed) { return BREED_DATA[breed] || null; },
    getAllBreeds: function() { return Object.keys(BREED_DATA); },
    onUpdate: onUpdate,
    PERSONALITIES: PERSONALITIES,
    VERSION: '1.0.0'
  };

  // Initialize integrations
  initAnalyzerIntegration();

  // Dispatch ready event
  try {
    window.dispatchEvent(new CustomEvent('prime:profile:ready'));
  } catch (e) {}

})();
